import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import { calculateTimesheetHours, formatHours } from '../utils/inputUtils';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../utils/env';
import { supabase } from '../utils/supabaseClient';
import { TIME_TYPES, TIME_TYPE_CONFIG, isJobRequired, getTimeTypesSorted } from '../constants/timeTypes';
import { showToast } from './Common/Toast';
import { companyDateKey, companyTimeKey } from '../utils/dateFormatters';
import {
  XMarkIcon,
  ClockIcon,
  UserIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  PencilIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import GlassModal from './Common/GlassModal';

const TimesheetFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  timesheet = null,
  employees = [],
  jobs = []
}) => {
  const { user } = useUser();
  // Default lunch for a NEW entry. There is no company-level lunch setting in the
  // schema (checked company_settings and companies), so this is a plain constant.
  const [defaultLunchMinutes] = useState(30);
  const [calculatedHours, setCalculatedHours] = useState({
    totalHours: 0,
    regularHours: 0,
    overtimeHours: 0,
    totalPaidHours: 0,
    lunchDeduction: 0
  });

  const [formData, setFormData] = useState({
    employee_id: '',
    job_id: '',
    work_date: companyDateKey(),
    clock_in: '09:00',
    clock_out: '17:00',
    // #6082 (ruled 2026-10-24): a new entry deducts no lunch until the box is ticked.
    lunch_taken: false,
    lunch_override_minutes: null,
    notes: '',
    // NEW Phase 1 fields
    time_type: 'job',
    source: 'manual',
    edit_reason: ''
  });
  const [loading, setLoading] = useState(false);
  // Board #1930, bounced twice. A clock-out at or before the clock-in used to wrap to the next
  // day on its own; labelling it (9f3f24e0a) still let a typo - 07:00 for 19:00 - save as a
  // 22-hour shift, and six such rows are live. Industry standard: that is an ERROR unless the
  // person says the shift ended the next day. Genuine 22:00-06:00 call-outs stay one tick away.
  const [endsNextDay, setEndsNextDay] = useState(() => Boolean(
    timesheet?.clock_in && timesheet?.clock_out
    && new Date(timesheet.clock_out).toDateString() !== new Date(timesheet.clock_in).toDateString()
  ));
  // The form re-initialises when the dialog opens or `timesheet` changes (#3357); keep this in step.
  useEffect(() => {
    if (!isOpen) return;
    setEndsNextDay(Boolean(
      timesheet?.clock_in && timesheet?.clock_out
      // #3349: company-timezone days, matching the list's own endsNextDay in Timesheets.js.
      // toDateString() is browser-local, so the tick could disagree with the "(+1 day)" the
      // row it was opened from displays.
      && companyDateKey(timesheet.clock_out) !== companyDateKey(timesheet.clock_in)
    ));
  }, [isOpen, timesheet]);
  const crossesMidnight = Boolean(formData.clock_in && formData.clock_out && formData.clock_out <= formData.clock_in);

  // #3349: the Job list is filtered twice over — to work orders in `scheduled`/`in_progress`,
  // and for a non-admin to the ones assigned to them. A time entry outlives both filters, so
  // editing one logged against a since-completed job (or against a colleague's job) found no
  // matching <option> and the select displayed "Select a job" next to a row that plainly had
  // one. formData.job_id was correct throughout, so nothing was lost on save — but the dialog
  // contradicted the row it came from, and picking a job to "fix" it reassigns the entry.
  // The row carries its own number and title, so it can supply the missing option.
  const jobOptions = useMemo(() => {
    const currentJobId = timesheet?.work_order_id || timesheet?.job_id;
    if (!currentJobId || jobs.some((job) => job.id === currentJobId)) return jobs;
    const label = timesheet.work_order_number
      ? `#${timesheet.work_order_number} – ${timesheet.job_title || 'Untitled'}`
      : (timesheet.job_title || 'Untitled');
    return [{ id: currentJobId, job_title: timesheet.job_title || 'Untitled', work_order_number: timesheet.work_order_number, displayLabel: label }, ...jobs];
  }, [jobs, timesheet]);

  // Look up employee_id for employees (they don't select from dropdown)
  // FIX: Use user.id for lookup, and normalize role comparison
  useEffect(() => {
    const resolveEmployeeId = async () => {
      if (user?.role?.toLowerCase() === 'employee' && user?.id) {
        const { data } = await supabase
          .from('employees')
          .select('id')
          .eq('user_id', user.id)
          .single();
        
        if (data?.id) {
          setFormData(prev => ({ ...prev, employee_id: data.id }));
        }
      }
    };
    
    resolveEmployeeId();
  }, [user?.role, user?.id]);

  // #3357: re-initialise every time the dialog OPENS, not only when `timesheet` changes. For
  // Log Time `timesheet` is always null, so Cancel then Log Time again showed the unsaved notes
  // and times from last time. Edit is unaffected: it re-reads the row it was opened on.
  useEffect(() => {
    if (!isOpen) return;
    if (timesheet) {
      // Editing existing timesheet
      setFormData({
        employee_id: timesheet.employee_id,
        job_id: timesheet.work_order_id || timesheet.job_id || '',
        work_date: timesheet.date || timesheet.work_date,
        // #3349: company timezone, so the dialog shows the same clock as the row it was
        // opened from (the list renders these through fmtTime). toTimeString() read the
        // browser's zone and disagreed with the row for any out-of-zone user.
        clock_in: companyTimeKey(timesheet.clock_in),
        clock_out: companyTimeKey(timesheet.clock_out),
        // break_duration is the ONLY persisted lunch value — employee_timesheets has
        // no lunch_taken / lunch_override_minutes column, so reading those loaded
        // undefined and the input fell back to defaultLunchMinutes. Any save then
        // rewrote the stored break to 30. Derive both from what is actually stored.
        lunch_taken: (timesheet.break_duration || 0) > 0,
        lunch_override_minutes: timesheet.break_duration || null,
        notes: timesheet.notes || '',
        // NEW Phase 1 fields
        time_type: timesheet.time_type || 'job',
        source: timesheet.source || 'manual',
        edit_reason: ''
      });
    } else {
      // Creating new timesheet - employee_id resolved in useEffect above
      setFormData(prev => ({
        ...prev,
        job_id: '',
        work_date: companyDateKey(),
        clock_in: '09:00',
        clock_out: '17:00',
        lunch_taken: false,
        lunch_override_minutes: null,
        notes: '',
        time_type: 'job',
        source: 'manual',
        edit_reason: ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, timesheet, user]);

  // Calculate hours whenever clock times or lunch settings change
  useEffect(() => {
    if (formData.clock_in && formData.clock_out) {
      // #6065: 0 is a real lunch length. `|| default` turned a typed 0 back into 30; only a
      // field left blank (null / '') means "use the default".
      const override = formData.lunch_override_minutes;
      const lunchMinutes = formData.lunch_taken
        ? (override === null || override === undefined || override === '' ? defaultLunchMinutes : Number(override))
        : 0;

      const hours = calculateTimesheetHours(
        formData.clock_in,
        formData.clock_out,
        formData.lunch_taken,
        lunchMinutes
      );

      setCalculatedHours(hours);
    } else {
      setCalculatedHours({
        totalHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        totalPaidHours: 0,
        lunchDeduction: 0
      });
    }
  }, [formData.clock_in, formData.clock_out, formData.lunch_taken, formData.lunch_override_minutes, defaultLunchMinutes]);

  const handleSubmit = async (e, submitForApproval = false) => {
    e.preventDefault();
    setLoading(true);

    try {
      // The `required` attributes on the fields below are decorative: every button here
      // is an onClick calling this function directly, so the form is never submitted and
      // the browser's constraint validation never runs. Nothing else checked, so an
      // untouched form saved a real row — Employee "Select an employee", Job "Select a
      // job", and the entry landed as Jerald Smith / No Job / 7h30m, counted in the
      // week's hours. Check here, because this is the one path all three buttons take.
      if (!formData.employee_id) {
        showToast('error', 'Choose who the time is for before logging it.');
        setLoading(false);
        return;
      }

      // #4901: same gap as above — the date/time inputs' `required` never fires, so a cleared
      // field saved a row with no clock-in or clock-out.
      if (!formData.work_date || !formData.clock_in || !formData.clock_out) {
        showToast('error', 'Enter the work date, clock-in and clock-out times.');
        setLoading(false);
        return;
      }

      // #1949: the textarea's `required` never fired (the button calls handleSubmit directly),
      // so edits saved with the parent's 'Manual edit' fallback as their audit reason.
      if (timesheet && !String(formData.edit_reason || '').trim()) {
        showToast('error', 'Enter an edit reason — it is kept in the audit trail for this entry.');
        setLoading(false);
        return;
      }

      if (crossesMidnight && !endsNextDay) {
        showToast('error', 'Clock-out is before clock-in. Fix the time, or tick "Shift ends the next day" if it ran past midnight.');
        setLoading(false);
        return;
      }

      if (isJobRequired(formData.time_type) && !formData.job_id) {
        showToast(
          'error',
          `Choose a job — ${TIME_TYPE_CONFIG[formData.time_type]?.label || 'this time type'} has to be logged against one.`
        );
        setLoading(false);
        return;
      }

      // Validation: Check for future dates
      // #4900: company-timezone day. toISOString() is the UTC day, so an entry logged in the
      // evening (west of UTC) defaulted to, and was checked against, tomorrow's date.
      const today = companyDateKey();
      if (formData.work_date > today) {
        showToast('error', 'Cannot log time for future dates');
        setLoading(false);
        return;
      }

      // Validation: Check for back-date limit (employees only)
      if (user?.role?.toLowerCase() === 'employee') {
        const sevenDaysAgo = companyDateKey(new Date(Date.now() - 7*24*60*60*1000));
        if (formData.work_date < sevenDaysAgo) {
          // Wording, not behaviour: the 7-day back-date guardrail for employees stays.
          // It used to say "require manager approval… contact your supervisor", which
          // pointed at a sign-off process that does not exist (P29 — nobody approves
          // hours). An owner or admin can still enter it, so say that instead.
          showToast('error', 'Time entries older than 7 days have to be added by an owner or admin. Ask them to log it for you.');
          setLoading(false);
          return;
        }
      }

      // Include calculated hours in the form data
      const submissionData = {
        ...formData,
        regular_hours: calculatedHours.regularHours,
        overtime_hours: calculatedHours.overtimeHours,
        total_hours: calculatedHours.totalPaidHours, // Map to correct schema field name
        break_duration: calculatedHours.lunchDeduction, // Persist computed break minutes (parent reads break_duration)
        // Convert lunch settings to the format expected by the database
        lunch_override_minutes: formData.lunch_taken && formData.lunch_override_minutes !== defaultLunchMinutes
          ? formData.lunch_override_minutes
          : null
      };

      await onSubmit(submissionData, submitForApproval);
    } catch (error) {
      console.error('Error submitting timesheet:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-2xl"
      closeOnOverlay={false}
      header={(
        <>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ClockIcon className="w-6 h-6 text-primary-600" />
            {timesheet ? 'Edit Time Entry' : 'Log Time'}
          </h2>
          <button type="button" aria-label="Close" title="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </>
      )}
    >

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Employee Selection - Only show for admins/owners creating new timesheets */}
          {user?.role?.toLowerCase() !== 'employee' && !timesheet && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Employee *
              </label>
              <select
                required
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select an employee</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.full_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show employee name when editing existing timesheet */}
          {timesheet && user?.role?.toLowerCase() !== 'employee' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <UserIcon className="w-4 h-4 inline mr-1" />
                Employee
              </label>
              <div className="w-full px-3 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-md text-gray-700">
                {/* #3349: the employee list can miss the entry's own employee (an archived
                    or since-removed one), and the dialog then read "Unknown Employee" for a
                    row the list beside it names correctly. Fall back to the name the row
                    already carries before giving up. */}
                {employees.find(emp => emp.id === formData.employee_id)?.full_name
                  || timesheet?.full_name || timesheet?.employee_name || 'Unknown Employee'}
              </div>
            </div>
          )}

          {/* STEP 1: Time Type Selector (FIRST - drives everything) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BriefcaseIcon className="w-4 h-4 inline mr-1" />
              Time Type *
            </label>
            <select
              required
              value={formData.time_type || TIME_TYPES.JOB}
              onChange={(e) => {
                const newTimeType = e.target.value;
                setFormData({
                  ...formData,
                  time_type: newTimeType,
                  // Clear job if not required for this time type
                  job_id: isJobRequired(newTimeType) ? formData.job_id : ''
                });
              }}
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {getTimeTypesSorted().map(timeType => {
                const config = TIME_TYPE_CONFIG[timeType];
                return (
                  <option key={timeType} value={timeType}>
                    {config.label} {config.jobRequired ? '(Job Required)' : '(Optional Job)'}
                  </option>
                );
              })}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {TIME_TYPE_CONFIG[formData.time_type]?.description}
            </p>
          </div>

          {/* STEP 2: Job Selection (conditionally shown based on time type) */}
          {isJobRequired(formData.time_type) ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BriefcaseIcon className="w-4 h-4 inline mr-1" />
                Job *
              </label>
              <select
                required
                value={formData.job_id}
                onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a job</option>
                {jobOptions.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.displayLabel || `#${job.work_order_number} – ${job.job_title}`}{job.status ? ` (${job.status})` : ''}
                  </option>
                ))}
              </select>
              {/* #2625: this was unconditional, so it showed in red beside a job that WAS
                  selected (e.g. Edit on a Job Work row). Only while the job is actually missing. */}
              {!formData.job_id && (
                <p className="mt-1 text-xs text-red-600">Job is required for {TIME_TYPE_CONFIG[formData.time_type]?.label}</p>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <BriefcaseIcon className="w-4 h-4 inline mr-1" />
                Job (Optional)
              </label>
              <select
                value={formData.job_id}
                onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">No specific job</option>
                {jobOptions.map(job => (
                  <option key={job.id} value={job.id}>
                    {job.displayLabel || `#${job.work_order_number} – ${job.job_title}`}{job.status ? ` (${job.status})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Work Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
              Work Date *
            </label>
            <input
              type="date"
              required
              value={formData.work_date}
              min={user?.role?.toLowerCase() === 'employee' ? companyDateKey(new Date(Date.now() - 7*24*60*60*1000)) : undefined}
              max={companyDateKey()}
              onChange={(e) => setFormData({ ...formData, work_date: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {/* #2625: "Select a date." read as an error beside a filled date; show it only when
                there is none. The employee window is a rule, so it always shows. */}
            {(user?.role?.toLowerCase() === 'employee' || !formData.work_date) && (
              <p className="mt-1 text-xs text-gray-500">
                {user?.role?.toLowerCase() === 'employee' ? 'You can log time for today and up to 7 days in the past.' : 'Select a date.'}
              </p>
            )}
          </div>

          {/* Clock In/Out Times */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock In *
              </label>
              <input
                type="time"
                required
                value={formData.clock_in}
                onChange={(e) => setFormData({ ...formData, clock_in: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock Out *
              </label>
              <input
                type="time"
                required
                value={formData.clock_out}
                onChange={(e) => setFormData({ ...formData, clock_out: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Lunch Deduction */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Lunch Break
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="lunch_taken"
                  checked={formData.lunch_taken}
                  onChange={(e) => setFormData({
                    ...formData,
                    lunch_taken: e.target.checked,
                    lunch_override_minutes: null // Reset override when toggling
                  })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="lunch_taken" className="text-sm text-gray-700">
                  Lunch taken
                </label>
              </div>
            </div>

            {formData.lunch_taken && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lunch Duration (minutes)
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={formData.lunch_override_minutes ?? defaultLunchMinutes}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const n = parseInt(raw, 10);
                      setFormData({
                        ...formData,
                        lunch_override_minutes: raw === '' || Number.isNaN(n) ? '' : Math.min(120, Math.max(0, n))
                      });
                    }}
                    onBlur={() => {
                      if (formData.lunch_override_minutes === '') {
                        setFormData({ ...formData, lunch_override_minutes: null });
                      }
                    }}
                    className="w-24 px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-500">
                    (Default: {defaultLunchMinutes} min)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Auto-Calculated Hours Summary */}
          {(formData.clock_in && formData.clock_out) && (
            <div className="bg-blue-500/10 backdrop-blur-sm border border-white/10 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Hours Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-700 font-medium">Regular:</span>
                  <div className="text-blue-900">{formatHours(calculatedHours.regularHours)}</div>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Overtime:</span>
                  <div className="text-blue-900">{formatHours(calculatedHours.overtimeHours)}</div>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Lunch:</span>
                  <div className="text-blue-900">{calculatedHours.lunchDeduction > 0 ? `-${calculatedHours.lunchDeduction}` : 0} min</div>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Total Paid:</span>
                  <div className="text-blue-900 font-semibold">{formatHours(calculatedHours.totalPaidHours)}</div>
                </div>
              </div>
              {/* Board #1930. A clock-out at or before the clock-in is treated as an OVERNIGHT
                  shift and stored on the next calendar day — correct for a 22:00-06:00 call-out,
                  and it must stay possible — but until now nothing said so anywhere on screen.
                  The word "overnight" appeared only in code comments, so a simple typo (07:00
                  instead of 19:00) silently became a 23-hour shift with no cue at all.
                  Stated, not blocked: labelling it is what makes it a decision rather than an
                  accident, and the hours beside it are what give the typo away. */}
              {crossesMidnight && (
                <div className="mt-2 space-y-1">
                  <label className="flex items-center gap-2 text-xs font-medium text-indigo-900 dark:text-indigo-200">
                    <input
                      type="checkbox"
                      checked={endsNextDay}
                      onChange={(e) => setEndsNextDay(e.target.checked)}
                    />
                    Shift ends the next day
                  </label>
                  {endsNextDay ? (
                    <div className="flex items-start text-indigo-800 dark:text-indigo-300">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="text-xs">
                        Overnight shift — counts as {formatHours(calculatedHours.totalPaidHours)}.
                      </span>
                    </div>
                  ) : (
                    <div role="alert" className="flex items-start text-red-700 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                      <span className="text-xs">
                        Clock-out is before clock-in. Fix the time, or tick the box if the shift ran past midnight.
                      </span>
                    </div>
                  )}
                </div>
              )}
              {calculatedHours.overtimeHours > 0 && (
                <div className="mt-2 flex items-center text-amber-700">
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  <span className="text-xs">Overtime detected (over 8 hours)</span>
                </div>
              )}
            </div>
          )}



          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <DocumentTextIcon className="w-4 h-4 inline mr-1" />
              Notes
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Add any notes about this work session..."
            />
          </div>

          {/* NEW Phase 1: Edit Reason (only when editing) */}
          {timesheet && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <DocumentTextIcon className="w-4 h-4 inline mr-1" />
                Edit Reason *
              </label>
              <textarea
                rows={2}
                required
                value={formData.edit_reason}
                onChange={(e) => setFormData({ ...formData, edit_reason: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-800/60 border border-white/20 dark:border-slate-700/40 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Why are you editing this time entry? (required for audit trail)"
              />
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-white/10 bg-white/5 backdrop-blur-sm">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>

            {/* Show different buttons based on whether editing or creating and user role */}
            {timesheet ? (
              /* Editing existing timesheet */
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
                onClick={(e) => handleSubmit(e, false)}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <ClockIcon className="w-4 h-4" />
                    {user?.role?.toLowerCase() === 'employee' ? 'Update Time Entry' : 'Admin Update'}
                  </>
                )}
              </button>
            ) : (
              /* Creating new timesheet */
              <>
                <button
                  type="button"
                  disabled={loading}
                  className="btn-secondary flex items-center gap-2"
                  onClick={(e) => handleSubmit(e, false)}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <PencilIcon className="w-4 h-4" />
                      Save as Draft
                    </>
                  )}
                </button>

                {/* The second button finalises the entry. It is NOT "submit for approval".
                    PRODUCT_DECISIONS P29: this is time TRACKING, not payroll — "nobody
                    signs off on hours, so there is nothing to approve or deny", and the
                    operator's ruling is "if approvals or approved_by isn't needed lets
                    cut it". The per-row Approve/Reject buttons went on 2026-08-16; this
                    label promised the same workflow and outlived them, so an employee was
                    told their hours were awaiting a sign-off that can never come.
                    Draft vs logged is kept — that distinction is about whether the entry
                    is finished, not about anyone approving it. */}
                {(true) && (
                  <button
                    type="button"
                    disabled={loading}
                    className="btn-primary flex items-center gap-2"
                    onClick={(e) => handleSubmit(e, true)}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <DocumentCheckIcon className="w-4 h-4" />
                        Log Time
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </form>
    </GlassModal>
  );
};

export default TimesheetFormModal;
